<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import AccountBusinessHoursAPI from 'dashboard/api/accountBusinessHours';
import BusinessHoursEditor from './components/BusinessHoursEditor.vue';
import AccountHolidaysManager from './components/AccountHolidaysManager.vue';

const { t } = useI18n();
const { updateAccount } = useAccount();

const businessHours = ref({
  working_hours_enabled: false,
  timezone: 'UTC',
  working_hours: [],
});
const isBusinessHoursEnabled = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);

const loadBusinessHours = async () => {
  isLoading.value = true;
  try {
    const response = await AccountBusinessHoursAPI.get();
    businessHours.value = response.data;
    isBusinessHoursEnabled.value = Boolean(response.data.working_hours_enabled);
  } finally {
    isLoading.value = false;
  }
};

const saveBusinessHours = async payload => {
  isSaving.value = true;
  try {
    const response = await AccountBusinessHoursAPI.update('', payload);
    businessHours.value = response.data;
    isBusinessHoursEnabled.value = Boolean(response.data.working_hours_enabled);
    await updateAccount(
      {
        account_timezone: response.data.timezone,
        working_hours_enabled: response.data.working_hours_enabled,
      },
      { silent: true }
    );
    useAlert(t('BUSINESS_HOURS.API.SUCCESS'));
  } catch {
    useAlert(t('BUSINESS_HOURS.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadBusinessHours);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-found="false"
    section-gap-class="gap-10"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BUSINESS_HOURS.PAGE_TITLE')"
        :description="$t('BUSINESS_HOURS.PAGE_DESCRIPTION')"
        feature-name="business-hours"
      />
    </template>

    <template #body>
      <div class="flex w-full min-w-0 max-w-5xl flex-col gap-0">
        <SectionLayout
          :title="$t('BUSINESS_HOURS.ACCOUNT_SECTION.TITLE')"
          :description="$t('BUSINESS_HOURS.ACCOUNT_SECTION.DESCRIPTION')"
        >
          <BusinessHoursEditor
            :value="businessHours"
            :is-saving="isSaving"
            :save-label="$t('BUSINESS_HOURS.SAVE_REFERENCE')"
            @update:enabled="isBusinessHoursEnabled = $event"
            @save="saveBusinessHours"
          />
        </SectionLayout>

        <SectionLayout
          v-if="isBusinessHoursEnabled"
          with-border
          :title="$t('BUSINESS_HOURS.HOLIDAYS.TITLE')"
          :description="$t('BUSINESS_HOURS.HOLIDAYS.DESCRIPTION')"
        >
          <AccountHolidaysManager />
        </SectionLayout>
      </div>
    </template>
  </SettingsLayout>
</template>
